import { createContext, useContext, ReactNode } from "react"
import { GivingPerson } from "../types"
import { useQuery } from "@tanstack/react-query"
import { fetchGivingPerson } from "../queries"
import { AlertMessage, Loading } from "source/shared/components"

type GivingPersonContextType = {
  givingPerson: GivingPerson
}

const GivingPersonContext = createContext<GivingPersonContextType | undefined>(
  undefined,
)

export const GivingPersonProvider = ({ children }: { children: ReactNode }) => {
  const {
    data: givingPerson,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["GivingPerson"],
    queryFn: () => fetchGivingPerson(),
  })

  if (isLoading) {
    return <Loading message="Loading" />
  }

  if (isError || !givingPerson) {
    return (
      <AlertMessage type="error" icon="general#exclamation-triangle">
        Something went wrong. Please try again.
      </AlertMessage>
    )
  }

  return (
    givingPerson && (
      <GivingPersonContext.Provider
        value={{
          givingPerson,
        }}
      >
        {children}
      </GivingPersonContext.Provider>
    )
  )
}

export const useGivingPersonContext = () => {
  const context = useContext(GivingPersonContext)
  if (!context) {
    throw new Error(
      "useGivingPersonContext must be used within a GivingPersonProvider",
    )
  }
  return context
}
